// BraceLayer.tsx — R3F component: renders brace pipe cylinders, anchor discs, insert spheres, and clash beacons.

import { useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import { Html } from '@react-three/drei';
import { useState } from 'react';
import * as THREE from 'three';
import type { BraceGeometry } from '../../utils/computeBraces';

const BRACE_NORMAL  = new THREE.Color(0x7a9ab5);
const BRACE_CLASH   = new THREE.Color(0xff4400);
const ANCHOR_COLOR  = new THREE.Color(0x888888);
const INSERT_NORMAL = new THREE.Color(0x7a9ab5);
const INSERT_CLASH  = new THREE.Color(0xff4400);
const BEACON_ERROR  = new THREE.Color(0xff0000);
const BEACON_WARN   = new THREE.Color(0xff4400);

// ── Per-brace pipe + insert sphere + anchor disc ───────────────────────────
function BracePipe({ brace }: { brace: BraceGeometry }) {
  const [hovered, setHovered] = useState(false);
  const color = brace.isClash ? BRACE_CLASH : BRACE_NORMAL;
  const iColor = brace.isClash ? INSERT_CLASH : INSERT_NORMAL;

  // Direction vector from insert to anchor
  const dx = brace.anchorX - brace.insertX;
  const dy = brace.anchorY - brace.insertY;
  const dz = brace.anchorZ - brace.insertZ;

  // Midpoint
  const mx = (brace.insertX + brace.anchorX) / 2;
  const my = (brace.insertY + brace.anchorY) / 2;
  const mz = (brace.insertZ + brace.anchorZ) / 2;

  // Quaternion: align +Y (CylinderGeometry default axis) → brace direction
  const dir = new THREE.Vector3(dx, dy, dz).normalize();
  const up  = new THREE.Vector3(0, 1, 0);
  const quat = new THREE.Quaternion().setFromUnitVectors(up, dir);

  const labelText = `${brace.panelMark} · ${Math.round(brace.length / 12)}′${Math.round(brace.length % 12)}″ @ ${Math.round(brace.angleDeg)}°`;

  return (
    <group>
      {/* Brace pipe */}
      <mesh
        position={[mx, my, mz]}
        quaternion={quat}
        onPointerEnter={e => { e.stopPropagation(); setHovered(true); }}
        onPointerLeave={() => setHovered(false)}
      >
        <cylinderGeometry args={[3.5, 3.5, brace.length, 8]} />
        <meshStandardMaterial color={color} roughness={0.4} metalness={0.6} />
      </mesh>

      {/* Insert sphere at panel end */}
      <mesh position={[brace.insertX, brace.insertY, brace.insertZ]}>
        <sphereGeometry args={[5, 8, 8]} />
        <meshStandardMaterial color={iColor} roughness={0.3} metalness={0.7} />
      </mesh>

      {/* Anchor disc at floor */}
      <mesh position={[brace.anchorX, brace.anchorY + 1, brace.anchorZ]}>
        <cylinderGeometry args={[12, 12, 2, 16]} />
        <meshStandardMaterial color={ANCHOR_COLOR} roughness={0.7} metalness={0.2} />
      </mesh>

      {hovered && (
        <Html
          center
          distanceFactor={400}
          position={[mx, my + 12, mz]}
          style={{ pointerEvents: 'none' }}
        >
          <div style={{
            background: brace.isClash ? '#aa2200' : '#1a3a5a',
            color: 'white', padding: '5px 9px', borderRadius: 4,
            fontSize: 11, fontWeight: 600, whiteSpace: 'nowrap',
            boxShadow: '0 2px 8px rgba(0,0,0,0.4)',
          }}>
            {labelText}
            {brace.isClash && <span style={{ marginLeft: 6, color: '#ffcc00' }}>⚠ {brace.clashType}</span>}
          </div>
        </Html>
      )}
    </group>
  );
}

// ── Pulsing clash beacon (mirrors ClashBeacon in SiteViewer3D) ────────────
function ClashBeacon({ x, y, z, type }: { x: number; y: number; z: number; type: 'brace-brace' | 'brace-panel' }) {
  const lightRef = useRef<THREE.PointLight>(null);
  const matRef   = useRef<THREE.MeshBasicMaterial>(null);
  const color = type === 'brace-panel' ? BEACON_ERROR : BEACON_WARN;
  const baseI = type === 'brace-panel' ? 5 : 3;

  useFrame(({ clock, invalidate }) => {
    const pulse = 0.75 + 0.25 * Math.sin(clock.elapsedTime * 4 * Math.PI);
    if (lightRef.current) lightRef.current.intensity = baseI * pulse;
    if (matRef.current) matRef.current.opacity = 0.5 + 0.5 * pulse;
    invalidate();
  });

  return (
    <group position={[x, y, z]}>
      <pointLight ref={lightRef} color={color} intensity={baseI} distance={600} decay={2} />
      <mesh>
        <sphereGeometry args={[8, 16, 16]} />
        <meshBasicMaterial ref={matRef} color={color} transparent opacity={0.8} depthWrite={false} />
      </mesh>
    </group>
  );
}

// ── Main export ─────────────────────────────────────────────────────────────
export default function BraceLayer({ braces }: { braces: BraceGeometry[] }) {
  return (
    <group>
      {braces.map(b => <BracePipe key={b.id} brace={b} />)}

      {braces
        .filter(b => b.isClash && b.clashType)
        .map(b => {
          // Place beacon at insert (top of brace, most visible location)
          return (
            <ClashBeacon
              key={`beacon-${b.id}`}
              x={b.insertX}
              y={b.insertY}
              z={b.insertZ}
              type={b.clashType!}
            />
          );
        })}
    </group>
  );
}
